import React from 'react';
import styled from 'styled-components';
import PhotoSlide from '../PhotoSlide';
import { ProductHeroProps } from '../../types';

const FALLBACK_BACKGROUND = '/images/backgrounds/erp-bg.mp4';

const getVideoMimeType = (src: string) => {
  if (src.endsWith('.webm')) {
    return 'video/webm';
  }

  if (src.endsWith('.ogg') || src.endsWith('.ogv')) {
    return 'video/ogg';
  }

  return 'video/mp4';
};

const ProductHero: React.FC<ProductHeroProps> = ({ product }) => {
  const backgroundSrc = product.backgroundImage || FALLBACK_BACKGROUND;
  const hasVideoBackground = /\.(mp4|webm|ogg|ogv)(\?.*)?$/i.test(backgroundSrc);

  return (
    <>
      <HeroSection>
        {hasVideoBackground ? (
          <BackgroundVideo
            key={backgroundSrc}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
          >
            <source src={backgroundSrc} type={getVideoMimeType(backgroundSrc)} />
          </BackgroundVideo>
        ) : (
          <BackgroundImage $backgroundImage={backgroundSrc} />
        )}
        <HeroOverlay />
        <HeroContent>
          <h1>{product.name}</h1>
          <p>{product.description}</p>
        </HeroContent>
        {product.images && product.images.length > 0 && (
          <SlideshowContainer>
            {product.images.length > 1 && (
              <PhotoSlide images={product.images} />
            )}
          </SlideshowContainer>
        )}
        {product.demoUrl && (
          <DemoInfo>
            <p>
              Visit live demo at{' '}
              <a href={product.demoUrl} target="_blank" className="text-blue-500" rel="noopener noreferrer">
                {product.demoUrl}
              </a>
            </p>
            <div>username: {product.demoUsername}</div>
            <div>Password: {product.demoPassword}</div>
          </DemoInfo>
        )}
      </HeroSection>
    </>
  );
};

export const ProductContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem 4rem;
`;

const HeroSection = styled.section`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  margin-bottom: 3rem;
  align-items: center;
  padding: 3rem;
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const BackgroundImage = styled.div<{ $backgroundImage: string }>`
  position: absolute;
  inset: 0;
  background-image: url(${({ $backgroundImage }) => $backgroundImage});
  background-size: cover;
  background-position: center 45%;
  opacity: 0.2;
  z-index: 0;
`;

const BackgroundVideo = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 45%;
  opacity: 0.2;
  z-index: 0;
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  /* background: linear-gradient(135deg, rgba(255, 255, 255, 0.82), rgba(255, 255, 255, 0.88)); */
  z-index: 1;
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;

  h1 {
    font-size: 2.5rem;
    margin-bottom: 1.5rem;
    color: var(--primary-color);
    line-height: 1.2;
  }
  
  p {
    font-size: 1.1rem;
    color: var(--text-dark);
    line-height: 1.6;
  }
  
  @media (max-width: 768px) {
    h1 {
      font-size: 2rem;
    }
  }
`;

const SlideshowContainer = styled.div`
  position: relative;
  width: 100%;
  z-index: 2;
`;

const DemoInfo = styled.div`
  position: relative;
  z-index: 2;
  color: var(--text-dark);

  p {
    margin-bottom: 0.5rem;
    line-height: 1.6;
  }

  a {
    color: var(--primary-color);
  }
`;


export default ProductHero; 